import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Save, Hash, Building2, Sparkles, X as XIcon, ArrowUp, ArrowDown, Plus, Mail, Send, ShieldCheck, ShieldAlert } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type {
    BillingSettings as BillingSettingsType, BillingSmtpSettings, InvoiceCatalogItem,
    InvoiceCounter, InvoiceCounterPreview, InvoiceLanguage, InvoiceType,
} from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';
import { fmtMoneyPlain } from './invoiceHelpers';

const inputClass =
    'w-full px-3 py-2 text-sm bg-white dark:bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const LANGS: InvoiceLanguage[] = ['de', 'es', 'en'];
const LANG_LABEL: Record<InvoiceLanguage, string> = { de: 'Deutsch', es: 'Español', en: 'English' };
const TYPE_LABEL: Record<InvoiceType, string> = { offer: 'Angebot', invoice: 'Rechnung', cancellation: 'Stornorechnung' };

const Section: React.FC<{ title: string; description?: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ title, description, icon, children }) => (
    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5">
        <div className="flex items-start gap-3 mb-4">
            {icon && <div className="mt-0.5 h-8 w-8 rounded-md bg-brand-accent/10 ring-1 ring-brand-accent/20 flex items-center justify-center text-brand-primary">{icon}</div>}
            <div>
                <h3 className="text-sm font-semibold text-brand-text">{title}</h3>
                {description && <p className="mt-0.5 text-xs text-brand-text-muted">{description}</p>}
            </div>
        </div>
        {children}
    </section>
);

const BillingSettings: React.FC = () => {
    const { addToast } = useToast();
    const [settings, setSettings] = useState<BillingSettingsType | null>(null);
    const [counters, setCounters] = useState<Record<InvoiceType, InvoiceCounter> | null>(null);
    const [previews, setPreviews] = useState<Record<InvoiceType, InvoiceCounterPreview> | null>(null);
    const [catalog, setCatalog] = useState<InvoiceCatalogItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const [smtp, setSmtp] = useState<BillingSmtpSettings | null>(null);

    const load = async () => {
        try {
            setLoading(true);
            const [s, c, cat, sm] = await Promise.all([
                apiService.getBillingSettings(),
                apiService.getInvoiceCounters(),
                apiService.listInvoiceCatalog({ activeOnly: true }),
                apiService.getBillingSmtp(),
            ]);
            setSettings(s.data.data);
            setCounters(c.data.data.counters);
            setPreviews(c.data.data.previews);
            setCatalog(cat.data.data || []);
            setSmtp(sm.data.data);
            setDirty(false);
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Einstellungen konnten nicht geladen werden', 'error');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    // ---- handlers ----

    const patchIssuer = (key: keyof BillingSettingsType['issuer'], value: string) => {
        setSettings(prev => prev ? ({ ...prev, issuer: { ...prev.issuer, [key]: value } }) : prev);
        setDirty(true);
    };
    const patchDefaults = (key: keyof BillingSettingsType['defaults'], value: any) => {
        setSettings(prev => prev ? ({ ...prev, defaults: { ...prev.defaults, [key]: value } }) : prev);
        setDirty(true);
    };
    const patchTextBlock = (block: 'payment_terms_text' | 'bank_details_text' | 'vat_exemption_text' | 'default_invoice_footer', lang: InvoiceLanguage, value: string) => {
        setSettings(prev => prev ? ({ ...prev, [block]: { ...prev[block], [lang]: value } }) : prev);
        setDirty(true);
    };
    const patchEmailTemplate = (lang: InvoiceLanguage, type: InvoiceType, field: 'subject' | 'body', value: string) => {
        setSettings(prev => {
            if (!prev) return prev;
            const cur = prev.email_templates?.[lang]?.[type] || { subject: '', body: '' };
            return {
                ...prev,
                email_templates: {
                    ...prev.email_templates,
                    [lang]: {
                        ...(prev.email_templates?.[lang] || {}),
                        [type]: { ...cur, [field]: value },
                    },
                } as any,
            };
        });
        setDirty(true);
    };
    const setQuickIds = (ids: number[]) => {
        setSettings(prev => prev ? ({ ...prev, quick_action_catalog_ids: ids }) : prev);
        setDirty(true);
    };

    const save = async () => {
        if (!settings) return;
        try {
            setSaving(true);
            await apiService.updateBillingSettings(settings);
            await load();
            addToast('Einstellungen gespeichert', 'success');
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen', 'error');
        } finally {
            setSaving(false);
        }
    };

    const updateCounter = async (type: InvoiceType, patch: Partial<InvoiceCounter>) => {
        try {
            await apiService.updateInvoiceCounter(type, patch);
            const c = await apiService.getInvoiceCounters();
            setCounters(c.data.data.counters);
            setPreviews(c.data.data.previews);
            addToast(`Nummernkreis ${TYPE_LABEL[type]} aktualisiert`, 'success');
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Nummernkreis konnte nicht geändert werden', 'error');
        }
    };

    const counterRows: InvoiceType[] = useMemo(() => ['invoice', 'offer', 'cancellation'], []);

    if (loading || !settings || !counters || !previews || !smtp) {
        return <div className="p-8 text-center text-sm text-brand-text-muted">Lade…</div>;
    }

    return (
        <div className="space-y-4">
            {/* SMTP / Versand-Account */}
            <Section
                icon={<Send className="h-4 w-4" strokeWidth={1.75} />}
                title="Versand-Account für Rechnungen"
                description={`Eigenes Postfach für Belege (Standard billing@seadent.es) — getrennt vom info@-Postfach der Buchungen. Bestätigt, dass Patientenantworten direkt im Buchhaltungs-Postfach landen.`}
            >
                <BillingSmtpEditor
                    smtp={smtp}
                    onChanged={async () => { const r = await apiService.getBillingSmtp(); setSmtp(r.data.data); }}
                />
            </Section>

            {/* Issuer */}
            <Section
                icon={<Building2 className="h-4 w-4" strokeWidth={1.75} />}
                title="Klinik-Absenderdaten"
                description="Diese Angaben erscheinen oben auf jedem Beleg. Pflichtfelder für DACH/ES-Rechnungen: Firma, NIF/USt-Nr, Anschrift."
            >
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                    <div className="sm:col-span-4">
                        <label className={labelClass}>Firmenname (inkl. Rechtsform)</label>
                        <input type="text" value={settings.issuer.name} onChange={(e) => patchIssuer('name', e.target.value)} className={inputClass} placeholder="z. B. SeaDent Mallorca SL" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelClass}>Rechtsform (optional)</label>
                        <input type="text" value={settings.issuer.legal_form} onChange={(e) => patchIssuer('legal_form', e.target.value)} className={inputClass} placeholder="SL / GmbH / …" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelClass}>NIF (Spanien)</label>
                        <input type="text" value={settings.issuer.nif} onChange={(e) => patchIssuer('nif', e.target.value)} className={inputClass} placeholder="B12345678" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelClass}>USt-IdNr (zusätzlich, optional)</label>
                        <input type="text" value={settings.issuer.vat_id} onChange={(e) => patchIssuer('vat_id', e.target.value)} className={inputClass} placeholder="ESB12345678" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelClass}>Ärztlicher Leiter (optional)</label>
                        <input type="text" value={settings.issuer.medical_director} onChange={(e) => patchIssuer('medical_director', e.target.value)} className={inputClass} />
                    </div>

                    <div className="sm:col-span-4">
                        <label className={labelClass}>Adresszeile 1</label>
                        <input type="text" value={settings.issuer.address_line1} onChange={(e) => patchIssuer('address_line1', e.target.value)} className={inputClass} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelClass}>Adresszeile 2 (optional)</label>
                        <input type="text" value={settings.issuer.address_line2} onChange={(e) => patchIssuer('address_line2', e.target.value)} className={inputClass} />
                    </div>
                    <div className="sm:col-span-1">
                        <label className={labelClass}>PLZ</label>
                        <input type="text" value={settings.issuer.postal_code} onChange={(e) => patchIssuer('postal_code', e.target.value)} className={inputClass} />
                    </div>
                    <div className="sm:col-span-3">
                        <label className={labelClass}>Stadt</label>
                        <input type="text" value={settings.issuer.city} onChange={(e) => patchIssuer('city', e.target.value)} className={inputClass} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelClass}>Land (ISO 2)</label>
                        <input type="text" value={settings.issuer.country} onChange={(e) => patchIssuer('country', e.target.value)} className={inputClass} maxLength={2} />
                    </div>

                    <div className="sm:col-span-2">
                        <label className={labelClass}>Telefon</label>
                        <input type="text" value={settings.issuer.phone} onChange={(e) => patchIssuer('phone', e.target.value)} className={inputClass} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelClass}>E-Mail</label>
                        <input type="email" value={settings.issuer.email} onChange={(e) => patchIssuer('email', e.target.value)} className={inputClass} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelClass}>Webseite (optional)</label>
                        <input type="text" value={settings.issuer.website} onChange={(e) => patchIssuer('website', e.target.value)} className={inputClass} />
                    </div>

                    <div className="sm:col-span-2">
                        <label className={labelClass}>Bank</label>
                        <input type="text" value={settings.issuer.bank_name} onChange={(e) => patchIssuer('bank_name', e.target.value)} className={inputClass} />
                    </div>
                    <div className="sm:col-span-3">
                        <label className={labelClass}>IBAN</label>
                        <input type="text" value={settings.issuer.iban} onChange={(e) => patchIssuer('iban', e.target.value)} className={inputClass} placeholder="ES91 2100 0418 4502 0005 1332" />
                    </div>
                    <div className="sm:col-span-1">
                        <label className={labelClass}>BIC</label>
                        <input type="text" value={settings.issuer.bic} onChange={(e) => patchIssuer('bic', e.target.value)} className={inputClass} />
                    </div>
                </div>
            </Section>

            {/* Defaults */}
            <Section
                icon={<Receipt className="h-4 w-4" strokeWidth={1.75} />}
                title="Voreinstellungen für neue Belege"
                description="Werden beim Anlegen vorbefüllt — pro Beleg überschreibbar."
            >
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div>
                        <label className={labelClass}>MwSt-Default</label>
                        <input type="number" min={0} max={100} value={settings.defaults.vat_rate} onChange={(e) => patchDefaults('vat_rate', Number(e.target.value))} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Zahlungsziel (Tage)</label>
                        <input type="number" min={0} max={365} value={settings.defaults.due_days} onChange={(e) => patchDefaults('due_days', Number(e.target.value))} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Angebot gültig (Tage)</label>
                        <input type="number" min={1} max={365} value={settings.defaults.offer_valid_days} onChange={(e) => patchDefaults('offer_valid_days', Number(e.target.value))} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Standardsprache</label>
                        <select value={settings.defaults.language} onChange={(e) => patchDefaults('language', e.target.value as InvoiceLanguage)} className={inputClass}>
                            {LANGS.map(l => <option key={l} value={l}>{LANG_LABEL[l]}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Währung (ISO)</label>
                        <input type="text" value={settings.defaults.currency} onChange={(e) => patchDefaults('currency', e.target.value.toUpperCase())} maxLength={3} className={inputClass} />
                    </div>
                </div>
            </Section>

            {/* Number ranges */}
            <Section
                icon={<Hash className="h-4 w-4" strokeWidth={1.75} />}
                title="Nummernkreise"
                description="Fortlaufende, getrennte Kreise. Die nächste Nummer kann bei der Erstmigration aus dem Altsystem gesetzt werden — danach besser nicht mehr anfassen."
            >
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead>
                            <tr className="text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted">
                                <th className="px-2 py-2 text-left">Typ</th>
                                <th className="px-2 py-2 text-left w-24">Präfix</th>
                                <th className="px-2 py-2 text-right w-20">Padding</th>
                                <th className="px-2 py-2 text-right w-32">Nächste Nr.</th>
                                <th className="px-2 py-2 text-right">Vorschau</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-border">
                            {counterRows.map(type => {
                                const c = counters[type];
                                return (
                                    <tr key={type}>
                                        <td className="px-2 py-3 text-sm font-medium text-brand-text">{TYPE_LABEL[type]}</td>
                                        <td className="px-2 py-2">
                                            <input
                                                type="text"
                                                defaultValue={c.prefix}
                                                onBlur={(e) => e.target.value !== c.prefix && updateCounter(type, { prefix: e.target.value })}
                                                className={inputClass + ' tabular-nums'}
                                                maxLength={6}
                                            />
                                        </td>
                                        <td className="px-2 py-2">
                                            <input
                                                type="number"
                                                min={1}
                                                max={10}
                                                defaultValue={c.pad}
                                                onBlur={(e) => Number(e.target.value) !== c.pad && updateCounter(type, { pad: Number(e.target.value) })}
                                                className={inputClass + ' text-right tabular-nums'}
                                            />
                                        </td>
                                        <td className="px-2 py-2">
                                            <input
                                                type="number"
                                                min={1}
                                                defaultValue={c.next}
                                                onBlur={(e) => Number(e.target.value) !== c.next && updateCounter(type, { next: Number(e.target.value) })}
                                                className={inputClass + ' text-right tabular-nums'}
                                            />
                                        </td>
                                        <td className="px-2 py-2 text-right">
                                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold tabular-nums rounded bg-brand-accent/10 ring-1 ring-brand-accent/30 text-brand-primary">
                                                {previews[type].number}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Texts */}
            <Section
                title="Standardtexte je Sprache"
                description="Werden auf neuen Belegen vorbefüllt. Beim Einzelbeleg überschreibbar."
            >
                <div className="space-y-5">
                    {([
                        { key: 'payment_terms_text' as const, label: 'Zahlungsbedingungen', hint: 'Erscheint unter den Summen bei Rechnungen.' },
                        { key: 'bank_details_text' as const, label: 'Bankverbindung (Override)', hint: 'Leer = automatisch aus Issuer-Daten oben generieren.' },
                        { key: 'default_invoice_footer' as const, label: 'Standard-Fußtext', hint: 'Vorschlag für den freundlichen Abschiedstext am Ende jeder Rechnung — pro Beleg editierbar.' },
                        { key: 'vat_exemption_text' as const, label: 'MwSt-Befreiungs-Hinweis', hint: 'Wird angezeigt, sobald eine Position 0 % MwSt hat.' },
                    ]).map(({ key, label, hint }) => (
                        <div key={key}>
                            <div className="flex items-baseline justify-between mb-2">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-text">{label}</h4>
                                <span className="text-[11px] text-brand-text-muted">{hint}</span>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                {LANGS.map(l => (
                                    <div key={l}>
                                        <label className={labelClass}>{LANG_LABEL[l]}</label>
                                        <textarea
                                            rows={3}
                                            value={settings[key][l] || ''}
                                            onChange={(e) => patchTextBlock(key, l, e.target.value)}
                                            className={inputClass}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Email templates */}
            <Section
                icon={<Mail className="h-4 w-4" strokeWidth={1.75} />}
                title="E-Mail-Anschreiben für Angebote &amp; Rechnungen"
                description={`Diese Texte werden beim Versand vorbefüllt. Pro Beleg lässt sich der Text im "Per Mail senden"-Dialog noch anpassen. Verfügbare Variablen: {{number}}, {{patient_name}}, {{total}}, {{due_date}}, {{valid_until}}, {{issuer_name}}.`}
            >
                <EmailTemplatesEditor
                    templates={settings.email_templates}
                    onPatch={patchEmailTemplate}
                />
            </Section>

            {/* Quick chips */}
            <Section
                icon={<Sparkles className="h-4 w-4" strokeWidth={1.75} />}
                title={`Schnellauswahl im „Behandlung abschließen"-Dialog`}
                description={`Bis zu 8 Katalogeinträge — werden im Termin-Workflow als 1-Klick-Buttons gezeigt, um häufige Leistungen blitzschnell zu erfassen.`}
            >
                <QuickChipPicker
                    catalog={catalog}
                    selected={settings.quick_action_catalog_ids}
                    onChange={setQuickIds}
                />
            </Section>

            {/* Save bar */}
            <div className="sticky bottom-0 z-10 -mx-4 px-4 py-3 bg-brand-surface/90 backdrop-blur border-t border-brand-border flex items-center justify-between">
                <span className="text-xs text-brand-text-muted">
                    {dirty ? 'Ungespeicherte Änderungen' : 'Alle Änderungen gespeichert'}
                </span>
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Save className="h-4 w-4" strokeWidth={2} />
                    {saving ? 'Speichere…' : 'Speichern'}
                </button>
            </div>
        </div>
    );
};

// --------------------------------------------------------------------
// QuickChipPicker — ordered selection of up to 8 catalog ids.
// Left column: already selected (in order, reorderable, removable).
// Right column: search + remaining catalog entries.
// --------------------------------------------------------------------

interface QuickChipPickerProps {
    catalog: InvoiceCatalogItem[];
    selected: number[];
    onChange: (ids: number[]) => void;
}

const MAX_QUICK = 8;

const QuickChipPicker: React.FC<QuickChipPickerProps> = ({ catalog, selected, onChange }) => {
    const [query, setQuery] = useState('');

    const byId = useMemo(() => {
        const m = new Map<number, InvoiceCatalogItem>();
        catalog.forEach(it => m.set(it.id, it));
        return m;
    }, [catalog]);

    const candidates = useMemo(() => {
        const q = query.trim().toLowerCase();
        return catalog.filter(it => {
            if (selected.includes(it.id)) return false;
            if (!q) return true;
            const hay = [it.code, it.name?.de, it.name?.es, it.name?.en].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        });
    }, [catalog, selected, query]);

    const move = (idx: number, dir: -1 | 1) => {
        const next = [...selected];
        const target = idx + dir;
        if (target < 0 || target >= next.length) return;
        [next[idx], next[target]] = [next[target], next[idx]];
        onChange(next);
    };
    const remove = (id: number) => onChange(selected.filter(x => x !== id));
    const add = (id: number) => {
        if (selected.length >= MAX_QUICK) return;
        onChange([...selected, id]);
    };

    if (catalog.length === 0) {
        return (
            <div className="rounded-md bg-brand-surface-2/60 p-4 text-sm text-brand-text-muted">
                Der Katalog ist leer. Lege zuerst Einträge an —{' '}
                <Link to="/admin/invoices/catalog" className="text-brand-primary underline cursor-pointer">
                    Katalog öffnen
                </Link>.
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Selected list */}
            <div>
                <div className="flex items-baseline justify-between mb-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-text">
                        Auswahl ({selected.length}/{MAX_QUICK})
                    </h4>
                </div>
                {selected.length === 0 ? (
                    <div className="rounded-md border border-dashed border-brand-border bg-brand-surface-2/40 p-4 text-sm text-brand-text-muted text-center">
                        Noch nichts gewählt. Rechts auswählen.
                    </div>
                ) : (
                    <ol className="space-y-1.5">
                        {selected.map((id, idx) => {
                            const it = byId.get(id);
                            const name = it?.name.de || it?.name.es || it?.name.en || `#${id} (nicht mehr im Katalog)`;
                            return (
                                <li key={id} className="flex items-center gap-2 px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md">
                                    <span className="text-xs text-brand-text-muted tabular-nums w-5">{idx + 1}.</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-brand-text truncate">{name}</div>
                                        {it && (
                                            <div className="text-[11px] text-brand-text-muted tabular-nums">
                                                {fmtMoneyPlain(it.default_price_cents)} € · MwSt {it.default_vat_rate}%
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-brand-text-muted hover:text-brand-text disabled:opacity-30 cursor-pointer" aria-label="Nach oben"><ArrowUp className="h-3.5 w-3.5" strokeWidth={1.75} /></button>
                                    <button onClick={() => move(idx, 1)} disabled={idx === selected.length - 1} className="p-1 text-brand-text-muted hover:text-brand-text disabled:opacity-30 cursor-pointer" aria-label="Nach unten"><ArrowDown className="h-3.5 w-3.5" strokeWidth={1.75} /></button>
                                    <button onClick={() => remove(id)} className="p-1 text-rose-600 hover:bg-rose-50 rounded cursor-pointer" aria-label="Entfernen"><XIcon className="h-3.5 w-3.5" strokeWidth={1.75} /></button>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>

            {/* Candidates */}
            <div>
                <div className="flex items-baseline justify-between mb-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-text">Katalog</h4>
                </div>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Suchen…"
                    className={inputClass + ' mb-2'}
                />
                <div className="max-h-72 overflow-y-auto border border-brand-border rounded-md divide-y divide-brand-border">
                    {candidates.length === 0 ? (
                        <div className="p-3 text-xs text-brand-text-muted text-center">
                            {query ? 'Keine Treffer.' : 'Alle aktiven Einträge bereits ausgewählt.'}
                        </div>
                    ) : (
                        candidates.map(it => {
                            const name = it.name.de || it.name.es || it.name.en || `#${it.id}`;
                            const disabled = selected.length >= MAX_QUICK;
                            return (
                                <button
                                    key={it.id}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => add(it.id)}
                                    className="w-full text-left px-3 py-2 hover:bg-brand-surface-2/60 transition-colors cursor-pointer flex items-center justify-between gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-brand-text truncate">{name}</div>
                                        <div className="text-[11px] text-brand-text-muted tabular-nums">
                                            {fmtMoneyPlain(it.default_price_cents)} € · MwSt {it.default_vat_rate}%
                                        </div>
                                    </div>
                                    <Plus className="h-3.5 w-3.5 text-brand-primary shrink-0" strokeWidth={1.75} />
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

// --------------------------------------------------------------------
// BillingSmtpEditor — credentials for the billing@seadent.es account
// --------------------------------------------------------------------

interface BillingSmtpEditorProps {
    smtp: BillingSmtpSettings;
    onChanged: () => Promise<void> | void;
}

const BillingSmtpEditor: React.FC<BillingSmtpEditorProps> = ({ smtp, onChanged }) => {
    const { addToast } = useToast();
    const [draft, setDraft] = useState<BillingSmtpSettings & { pass?: string }>(smtp);
    const [saving, setSaving] = useState(false);
    const [testTo, setTestTo] = useState('');
    const [testing, setTesting] = useState(false);

    useEffect(() => { setDraft({ ...smtp, pass: undefined }); }, [smtp]);

    const save = async () => {
        try {
            setSaving(true);
            const payload: any = {
                host: draft.host,
                port: draft.port,
                secure: draft.secure,
                user: draft.user,
                from_name: draft.from_name,
            };
            if (draft.pass && draft.pass.length > 0) payload.pass = draft.pass;
            await apiService.updateBillingSmtp(payload);
            await onChanged();
            addToast('Versand-Account gespeichert', 'success');
            setDraft(prev => ({ ...prev, pass: '' }));
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen', 'error');
        } finally { setSaving(false); }
    };

    const test = async () => {
        if (!testTo.trim()) { addToast('Empfänger fehlt', 'error'); return; }
        try {
            setTesting(true);
            await apiService.testBillingSmtp(testTo.trim());
            addToast(`Test-Mail an ${testTo} versendet`, 'success');
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Test fehlgeschlagen', 'error');
        } finally { setTesting(false); }
    };

    return (
        <div className="space-y-4">
            <div className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ${smtp.passSet ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-900 border border-amber-200'}`}>
                {smtp.passSet
                    ? <><ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.75} /><div>Passwort hinterlegt. Versand bereit.</div></>
                    : <><ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.75} /><div>Noch kein Passwort gesetzt — bitte unten eintragen und speichern. Erst danach funktioniert „Per Mail senden".</div></>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                <div className="sm:col-span-3">
                    <label className={labelClass}>Anzeigename (Absender)</label>
                    <input type="text" value={draft.from_name} onChange={(e) => setDraft({ ...draft, from_name: e.target.value })} className={inputClass} placeholder="SeaDent Mallorca · Billing" />
                </div>
                <div className="sm:col-span-3">
                    <label className={labelClass}>E-Mail-Adresse</label>
                    <input type="email" value={draft.user} onChange={(e) => setDraft({ ...draft, user: e.target.value })} className={inputClass} placeholder="billing@seadent.es" />
                </div>
                <div className="sm:col-span-3">
                    <label className={labelClass}>SMTP-Host</label>
                    <input type="text" value={draft.host} onChange={(e) => setDraft({ ...draft, host: e.target.value })} className={inputClass} placeholder="w014e07b.kasserver.com" />
                </div>
                <div className="sm:col-span-1">
                    <label className={labelClass}>Port</label>
                    <input type="number" value={draft.port} onChange={(e) => setDraft({ ...draft, port: parseInt(e.target.value || '587', 10) })} className={inputClass} />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelClass}>Verbindung</label>
                    <select value={draft.secure ? 'tls' : 'starttls'} onChange={(e) => setDraft({ ...draft, secure: e.target.value === 'tls' })} className={inputClass}>
                        <option value="starttls">STARTTLS (Port 587)</option>
                        <option value="tls">Implicit TLS (Port 465)</option>
                    </select>
                </div>
                <div className="sm:col-span-6">
                    <label className={labelClass}>Passwort {smtp.passSet && <span className="ml-2 text-[10px] text-brand-text-muted normal-case">(leer lassen, um bestehendes Passwort zu behalten)</span>}</label>
                    <input
                        type="password"
                        value={draft.pass || ''}
                        onChange={(e) => setDraft({ ...draft, pass: e.target.value })}
                        className={inputClass}
                        autoComplete="new-password"
                        placeholder={smtp.passSet ? '••••••••' : 'Passwort vom Hosting-Anbieter'}
                    />
                </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-2 border-t border-brand-border">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50"
                >
                    <Save className="h-3.5 w-3.5" strokeWidth={2} />
                    {saving ? 'Speichere…' : 'Account speichern'}
                </button>

                <div className="flex-1 grid grid-cols-[1fr_auto] gap-2 max-w-md">
                    <input
                        type="email"
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        placeholder="test@adresse.de"
                        className={inputClass}
                    />
                    <button
                        type="button"
                        onClick={test}
                        disabled={testing || !smtp.passSet}
                        title={!smtp.passSet ? 'Erst Passwort speichern' : ''}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-primary border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send className="h-3.5 w-3.5" strokeWidth={1.75} />
                        {testing ? 'Sende…' : 'Test'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --------------------------------------------------------------------
// EmailTemplatesEditor — tabbed editor (language × doc type)
// --------------------------------------------------------------------

interface EmailTemplatesEditorProps {
    templates: Record<InvoiceLanguage, Record<InvoiceType, { subject: string; body: string }>>;
    onPatch: (lang: InvoiceLanguage, type: InvoiceType, field: 'subject' | 'body', value: string) => void;
}

const TYPE_TABS: { key: InvoiceType; label: string }[] = [
    { key: 'invoice', label: 'Rechnung' },
    { key: 'offer', label: 'Angebot' },
    { key: 'cancellation', label: 'Storno' },
];
const LANG_TABS: { key: InvoiceLanguage; label: string }[] = [
    { key: 'de', label: 'DE' },
    { key: 'es', label: 'ES' },
    { key: 'en', label: 'EN' },
];

const EmailTemplatesEditor: React.FC<EmailTemplatesEditorProps> = ({ templates, onPatch }) => {
    const [lang, setLang] = useState<InvoiceLanguage>('de');
    const [type, setType] = useState<InvoiceType>('invoice');

    const tpl = templates?.[lang]?.[type] || { subject: '', body: '' };

    return (
        <div>
            <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="inline-flex rounded-md border border-brand-border bg-brand-surface-2/40 p-0.5">
                    {LANG_TABS.map(t => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setLang(t.key)}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${
                                lang === t.key ? 'bg-brand-surface shadow-sm text-brand-text' : 'text-brand-text-muted hover:text-brand-text'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="inline-flex rounded-md border border-brand-border bg-brand-surface-2/40 p-0.5">
                    {TYPE_TABS.map(t => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setType(t.key)}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${
                                type === t.key ? 'bg-brand-surface shadow-sm text-brand-text' : 'text-brand-text-muted hover:text-brand-text'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-3">
                <div>
                    <label className={labelClass}>Betreff</label>
                    <input
                        type="text"
                        value={tpl.subject}
                        onChange={(e) => onPatch(lang, type, 'subject', e.target.value)}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className={labelClass}>Nachricht</label>
                    <textarea
                        rows={9}
                        value={tpl.body}
                        onChange={(e) => onPatch(lang, type, 'body', e.target.value)}
                        className={inputClass + ' font-mono text-[12.5px]'}
                    />
                </div>
            </div>
        </div>
    );
};

export default BillingSettings;
